import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import Panel from 'utils/panel';
import TrapFocus from 'utils/trap-focus';
import CloseIcon from 'utils/icon/icon-close.svg';
import Image from 'utils/image';

// Extract text from howToMeasureContent
const extractText = (node) => {
  let text = '';

  if (node.type === 'text') {
    text += node.value;
  } else if (node.children && Array.isArray(node.children)) {
    node.children.forEach((child) => {
      text += extractText(child);
    });
  }

  return text;
};

const ProductSizeGuide = (product) => {
  const { sizeGuide } = product;
  const closeTriggerEl = useRef(null);

  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: null,
    closeTriggerEl: null,
  });

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const openTriggerEl = document.querySelector('[data-size-guide-trigger]');
    if (openTriggerEl) {
      setPanelTriggers((value) => ({
        ...value,
        openTriggerEl,
      }));
    }
  }, []);

  useEffect(() => {
    if (closeTriggerEl.current) {
      setPanelTriggers((value) => ({
        ...value,
        closeTriggerEl: closeTriggerEl.current,
      }));
    }
  }, [closeTriggerEl]);

  let trapFocus = null;

  useEffect(() => {
    if (isOpen) {
      trapFocus = new TrapFocus(document.querySelector('[data-size-guide]'));
      document.body.classList.add('scroll-lock');
      trapFocus.init();

      // Target the Kustomer chat iFrame
      const kustomerIframe = document.querySelector('#kustomer-ui-sdk-iframe');
      if(kustomerIframe) kustomerIframe.classList.toggle("kustomer-chat-window-left");
    } else {
      const kustomerIframe = document.querySelector('#kustomer-ui-sdk-iframe');
      if(kustomerIframe) kustomerIframe.classList.toggle("kustomer-chat-window-left")
    }

    return () => {
      document.body.classList.remove('scroll-lock');
      if (trapFocus) trapFocus.destroy(document.querySelector('[data-size-guide-trigger]'));
    };
  }, [isOpen]);

  const tableHeaders = sizeGuide?.table?.length > 0 ? Object.keys(sizeGuide.table[0]) : [];
  const howToMeasureText = sizeGuide?.howToMeasureContent ? extractText(sizeGuide.howToMeasureContent) : '';

  return (
    <Panel
      triggers={panelTriggers}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      context="size-guide"
      showBackdrop={true}
      additionalClasses="w-full max-w-[420px]"
    >
      <div
        className="relative flex h-full flex-col"
        data-size-guide
      >
        <button
          className="text-smokey-blue absolute right-15 top-20 lg:right-20"
          ref={closeTriggerEl}
          aria-label={window.TMW.Strings.cart.close}
        >
          <CloseIcon />
        </button>
        <h4 className="border-b border-grey-5 px-15 px-20 py-20 text-faded-black">
          {window.TMW.Strings.product.sizeGuide}
        </h4>
        <div className="flex h-auto grow flex-col gap-20 overflow-y-auto pb-20 md:gap-15 md:pb-30">
          <Image
            {...sizeGuide.image?.previewImage}
            className="w-full object-cover"
          />
          <div className="h-full overflow-hidden px-15 md:px-20">
            <div className="h-full overflow-auto">
              <table className="h-full w-full table-auto border-separate border-spacing-0">
                <thead className="sticky top-0 z-10 bg-grey-6">
                  <tr>
                    {tableHeaders.map((header, index) => (
                      <th
                        key={header}
                        className={`body-14-med border border-grey-5 bg-grey-6 px-15 py-15 text-primary ${index === 0 ? 'sticky left-0 bg-grey-6' : ''}`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sizeGuide?.table?.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {tableHeaders.map((header, colIndex) => (
                        <td
                          key={colIndex}
                          className={
                            colIndex === 0
                              ? 'body-14-med sticky left-0 whitespace-nowrap border border-grey-5 bg-grey-6 px-15 py-15 text-center text-primary'
                              : 'body-12-light whitespace-nowrap border border-grey-5 bg-white px-15 py-15 text-center text-primary'
                          }
                        >
                          {row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {howToMeasureText && <p className="body-12-light pt-15 text-black md:pt-20">{howToMeasureText}</p>}
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default ProductSizeGuide;
